import { IncidentSchema } from '../chunk-6XJKVJAV.js';
import { normalizeAddress } from '../chunk-4RYIEGE7.js';
import { toCanonicalChainId } from '../chunk-3S6JE4D5.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';
import { z } from 'zod';

// src/incidents/notice.ts
function toneFor(notice) {
  if (!notice.references?.length) return "info";
  switch (notice.severity) {
    case "critical":
      return "critical";
    case "high":
    case "medium":
      return "warning";
    default:
      return "info";
  }
}
function isSubjectOfHarm(notice) {
  return notice.role === "victim" || notice.role === "recovery";
}
function chipLabelFor(notice) {
  const role = String(notice.role ?? "");
  const named = {
    exploiter: "Reported exploiter",
    intermediary: "Reported in exploit flow",
    cashout: "Reported cash-out",
    victim: "Exploit target",
    recovery: "Recovered funds"
  };
  return named[role] ?? "Named in an exploit report";
}
function bannerPropsFor(notice) {
  return {
    incidentId: notice.incident_id,
    tone: toneFor(notice),
    chipLabel: chipLabelFor(notice),
    headline: notice.headline ?? "",
    note: notice.note ?? "",
    summary: notice.summary ?? "",
    subjectOfHarm: isSubjectOfHarm(notice),
    references: notice.references ?? []
  };
}
function bannersFor(notices) {
  return (notices ?? []).filter((notice) => Boolean(notice?.headline)).map(bannerPropsFor);
}

// src/incidents/incident-catalog.generated.json
var incident_catalog_generated_default = {
  incidents: [
    {
      addresses: [
        {
          address: "0x770b10b273fc44fe9197d6bf20f145c2e98463ee",
          chain_id: "eip155:1",
          label: "",
          note: "Named in the case record (Bitquery transfer ledger) as receiving funds directly from the Bitget hot wallet on 2026-09-24. Reported, not independently verified: no public post-mortem or venue statement is cited. Received the 18:31 UTC transfers.",
          role: "exploiter"
        },
        {
          address: "0xf7bc92103f23ef312658cd9b81dc2713f7b396c3",
          chain_id: "eip155:1",
          label: "",
          note: "Named in the case record (Bitquery transfer ledger) as receiving funds directly from the Bitget hot wallet on 2026-09-24. Reported, not independently verified: no public post-mortem or venue statement is cited. Received the 18:58 UTC transfers.",
          role: "exploiter"
        },
        {
          address: "0xe410a2e5710ee787bcaa63f52a3943ff71f0d946",
          chain_id: "eip155:1",
          label: "Bitget Exploiter 6",
          note: "Named in the case record (Bitquery transfer ledger) as receiving funds directly from the Bitget hot wallet on 2026-09-24. Reported, not independently verified: no public post-mortem or venue statement is cited. Explorer-labelled 'Bitget Exploiter 6'.",
          role: "exploiter"
        },
        {
          address: "0x94a43df7687a8494948be937400e9d5d33135da0",
          chain_id: "eip155:1",
          label: "Bitget Exploiter 7",
          note: "Named in the case record (Bitquery transfer ledger) as receiving funds directly from the Bitget hot wallet on 2026-09-24. Reported, not independently verified: no public post-mortem or venue statement is cited. Explorer-labelled 'Bitget Exploiter 7'.",
          role: "exploiter"
        },
        {
          address: "0x1ab4973a48dc892cd9971ece8e01dcc7688f8f23",
          chain_id: "eip155:1",
          label: "Bitget hot wallet",
          note: "The Bitget hot wallet the funds were taken from, per the case record's transfer ledger. The venue is the loser here, not a party to the exploit; its other traffic is ordinary custody.",
          role: "victim"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "bitget_hot_wallet_2026_09",
      name: "Bitget",
      occurred_on: "2026-09-24",
      references: [
        {
          handle: "",
          label: "Case record: Bitquery transfer ledger, 2026-09-24 18:31 and 18:58 UTC",
          url: "https://explorer.bitquery.io/ethereum/address/0x1ab4973a48dc892cd9971ece8e01dcc7688f8f23"
        }
      ],
      severity: "critical",
      summary: "Funds left a Bitget hot wallet on Ethereum for four wallets in two bursts on the evening of 2026-09-24, per the case record's transfer ledger. Reported, not verified.",
      window_end: "",
      window_start: "2026-09-24T18:00:00Z"
    },
    {
      addresses: [
        {
          address: "0x51e9d833ecae4e8d9d8be17300aee6d3398c135d",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x96244d83dc15d36847c35209bbdc5bdde9bec3d8",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x83c7678492d623fb98834f0fbcb2e7b7f5af8950",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x83ef5e80fad88288f770152875ab0bb16641a09e",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xaf620e6d32b1c67f3396ef5d2f7d7642dc2e6ce9",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x3a21f4e6bbe527d347ca7c157f4233c935779847",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xfa3fcccb897079fd83bfba690e7d47eb402d6c49",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xfc926659dd8808f6e3e0a8d61b20b871f3fa6465",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xb172f7e99452446f18ff49a71bfeecf0873003b4",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x6d46bd3aff100f23c194e5312f93507978a6dc91",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xf0a16603289eaf35f64077ba3681af41194a1c09",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x23db729908137cb60852f2936d2b5c6de0e1c887",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x40e98feeebad7ddb0f0534ccaa617427ea10187e",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x140c9ab92347734641b1a7c124ffdee58c20c3e3",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x684d4b58dc32af786bf6d572a792ff7a883428b9",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xbc3e5e8c10897a81b63933348f53f2e052f89a7e",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x5af75eab6bec227657fa3e749a8bfd55f02e4b1d",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xbca02b395747d62626a65016f2e64a20bd254a39",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x4c198b3b5f3a4b1aa706dac73d826c2b795ccd67",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xcd7ec020121ead6f99855cbb972df502db5bc63a",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xbde2cc5375fa9e0383309a2ca31213f2d6cabcbd",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xd3c611aed139107dec2294032da3913bc26507fb",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xb72334cb9d0b614d30c4c60e2bd12ff5ed03c305",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x8c7235e1a6eef91b980d0fca083347fbb7ee1806",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x1bb0970508316dc735329752a4581e0a4babc6b4",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x1eb27f136bfe7947f80d6cee3cf0bfdf92b45e57",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xcd1a4a457ca8b0931c3bf81df3cfa227adbdb6e9",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x09278b36863be4ccd3d0c22d643e8062d7a11377",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x660bfcea3a5faf823e8f8bf57dd558db034dea1d",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xe9bc552fdfa54b30296d95f147e3e0280ff7f7e6",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x30a822cdd2782d2b2a12a08526452e885978fa1d",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xb4a862a81abb2f952fca4c6f5510962e18c7f1a2",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x0e8c1e2881f35ef20343264862a242fb749d6b35",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x9271eddda0f0f2bb7b1a0c712bdf8dbd0a38d1ab",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xe69753ddfbedbd249e703eb374452e78dae1ae49",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x2290937a4498c96effb87b8371a33d108f8d433f",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x959c4ca19c4532c97a657d82d97accbab70e6fb4",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x52207ec7b1b43aa5db116931a904371ae2c1619e",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x9ef42873ae015aa3da0c4354aef94a18d2b3407b",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x1542368a03ad1f03d96d51b414f4738961cf4443",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x21032176b43d9f7e9410fb37290a78f4fed6044c",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xa4b2fd68593b6f34e51cb9edb66e71c1b4ab449e",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x55cca2f5eb07907696afe4b9db5102bce5feb734",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xa5a023e052243b7cce34cbd4ba20180e8dea6ad6",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xdd90071d52f20e85c89802e5dc1ec0a7b6475f92",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x1512fcb09463a61862b73ec09b9b354af1790268",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xf302572594a68aa8f951fae64ed3ae7da41c72be",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0x723a7084028421994d4a7829108d63ab44658315",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xf03afb1c6a11a7e370920ad42e6ee735dbedf0b1",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xeb0baa3a556586192590cad296b1e48df62a8549",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        },
        {
          address: "0xd5b58cf7813c1edc412367b97876bd400ea5c489",
          chain_id: "eip155:1",
          label: "",
          note: 'Listed by the FBI in PSA I-022625-PSA (2025-02-26) among the Ethereum addresses "operated by or closely connected to North Korean TraderTraitor actors" after the Bybit theft.',
          role: "intermediary"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "bybit_cold_wallet_2025",
      name: "Bybit",
      occurred_on: "2025-02-21",
      references: [
        {
          handle: "",
          label: "FBI (IC3) PSA I-022625-PSA",
          url: "https://www.ic3.gov/PSA/2025/PSA250226"
        }
      ],
      severity: "critical",
      summary: "The theft of about $1.5 billion in ETH and staked ETH from a Bybit cold wallet, which the FBI attributed to North Korea's TraderTraitor actors.",
      window_end: "",
      window_start: ""
    },
    {
      addresses: [
        {
          address: "0x9ae755d23fc948fe94c9364a2398fd508a2ab0d2",
          chain_id: "eip155:1",
          label: "",
          note: "Primary wallet. Bought NES on Ethereum, bridged to Nesa, inflated the balance via the Cosmos EVM vulnerability, bridged back and dispersed the proceeds through a wallet network.",
          role: "exploiter"
        },
        {
          address: "0x80c17107e7929e2440c9e528466776ec71329e89",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0x9b49e12a68185b9b5cc2ec469e9ef43719f51c7c",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0x231bff3c5fce5c61d1055cae3a20b460eff13901",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0x20cd837cb50db8ded885fe3e799a2355d08a213f",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0xb92df70f3d25ed25265c7c341c9d2550c42ff83a",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0x2816039940fd2ea2ecafce843cb2cc39e73f3fd6",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0x83c85c3c045aedebd81a678a3ffd52794af6b414",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0x5d4de48e97f9255c43193515b2b354e399f997b6",
          chain_id: "eip155:1",
          label: "",
          note: "One of the eight wallets listed by Bubblemaps in the reported NES-to-ETH dispersal network. Cluster membership is a source claim, not independent proof of common ownership, complete transaction coverage, or an exchange sale.",
          role: "intermediary"
        },
        {
          address: "0xc810009a4f92ff10039a9ea9c705d2634a70446e",
          chain_id: "eip155:1",
          label: "",
          note: "The last non-custodial hop: gathered from several branches and sent repeated large ETH transfers into the KuCoin 17 hot wallet. Carries no exchange label of its own \u2014 its behaviour is consistent with either a per-user deposit address or an attacker-controlled feeder, and the public reporting does not settle which.",
          role: "cashout"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "nesa_cosmos_evm_balance_inflation",
      name: "Nesa / Cosmos EVM balance inflation",
      occurred_on: "2026-08-24",
      references: [
        {
          handle: "@bubblemaps",
          label: "Bubblemaps",
          url: "https://x.com/bubblemaps/status/2092613254912852356"
        },
        {
          handle: "@Rarma_",
          label: "Rarma \u2014 Ethereum bridge receipts and reported exit trades",
          url: "https://x.com/Rarma_/status/2092001755559309523"
        },
        {
          handle: "",
          label: "Cosmos security advisory GHSA-7g4w-cg88-2cq2",
          url: "https://github.com/cosmos/security/blob/main/communications/cosmos_evm_GHSA-7g4w-cg88-2cq2_post_mortem.md"
        }
      ],
      severity: "critical",
      summary: "A balance-inflation bug in Cosmos EVM let an attacker multiply a bridged NES position roughly 200x on Nesa Chain, bridge it back to Ethereum and sell it into the open market.",
      window_end: "",
      window_start: ""
    },
    {
      addresses: [
        {
          address: "0x098b716b8aaf21512996dc57eb0615e2383e2f96",
          chain_id: "eip155:1",
          label: "",
          note: "Designated by OFAC on 2022-04-14, under the Lazarus Group entry, as the address that received the funds taken from the Ronin bridge.",
          role: "exploiter"
        },
        {
          address: "0x3cffd56b47b7b41c56258d9c7731abadc360e073",
          chain_id: "eip155:1",
          label: "",
          note: "Designated by OFAC on 2022-04-22, under the Lazarus Group entry, as one of three addresses that interacted with the drainer 0x098b\u20262f96 and held stolen ETH. On chain it received 10,129.935 ETH from the drainer on 2022-04-18 (tx 0xa04e54\u20266d29).",
          role: "intermediary"
        },
        {
          address: "0xa0e1c89ef1a489c9c7de96311ed5ce5d32c20e4b",
          chain_id: "eip155:1",
          label: "",
          note: "Designated by OFAC on 2022-04-22, under the Lazarus Group entry, as one of three addresses that interacted with the drainer 0x098b\u20262f96 and held stolen ETH. On chain it received 18,256.81 ETH from the drainer on 2022-04-19 (tx 0xa0b693\u2026230f).",
          role: "intermediary"
        },
        {
          address: "0x53b6936513e738f44fb50d2b9476730c0ab3bfc1",
          chain_id: "eip155:1",
          label: "",
          note: "Designated by OFAC on 2022-04-22, under the Lazarus Group entry, as one of three addresses that interacted with the drainer 0x098b\u20262f96 and held stolen ETH. On chain it received 21,629.48215 ETH from the drainer on 2022-04-21 (tx 0x899ad3\u202611d4).",
          role: "intermediary"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "ronin_bridge_2022",
      name: "Ronin bridge",
      occurred_on: "2022-03-23",
      references: [
        {
          handle: "",
          label: "US Treasury (OFAC) SDN list",
          url: "https://home.treasury.gov/policy-issues/financial-sanctions/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists"
        },
        {
          handle: "",
          label: "Elliptic",
          url: "https://www.elliptic.co/blog/further-sanctions-against-north-koreas-lazarus-group-for-laundering-stolen-ronin-funds"
        },
        {
          handle: "",
          label: "CyberScoop",
          url: "https://cyberscoop.com/ronin-bridge-hack-lazarus-group-north-korea-treasury-sanctions/"
        }
      ],
      severity: "critical",
      summary: "The theft of 173,600 ETH and 25.5M USDC from the Ronin bridge behind Axie Infinity, which the US Treasury attributed to North Korea's Lazarus Group.",
      window_end: "",
      window_start: ""
    }
  ]
};

// src/incidents/catalog.ts
var CatalogSchema = z.object({ incidents: z.array(IncidentSchema).default([]) });
var BUNDLED_INCIDENTS = Object.freeze(
  [...CatalogSchema.parse(incident_catalog_generated_default).incidents].sort((a, b) => {
    const left = a.occurred_on || "";
    const right = b.occurred_on || "";
    if (left === right) return a.id.localeCompare(b.id);
    if (!left) return 1;
    if (!right) return -1;
    return right.localeCompare(left);
  })
);
function bundledIncident(id) {
  return BUNDLED_INCIDENTS.find((incident) => incident.id === id);
}
function incidentAccounts(incident) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const entry of incident.addresses ?? []) {
    const address = String(entry.address ?? "").trim();
    const chain = String(entry.chain_id ?? "").trim();
    if (!address || !chain) continue;
    const account = `${toCanonicalChainId(chain) ?? chain}:${normalizeAddress(address, chain)}`;
    if (seen.has(account)) continue;
    seen.add(account);
    out.push(account);
  }
  return out;
}
function incidentRoleCounts(incident) {
  const counts = {};
  for (const entry of incident.addresses ?? []) {
    const role = String(entry.role ?? "") || "unknown";
    counts[role] = (counts[role] ?? 0) + 1;
  }
  return counts;
}

export { BUNDLED_INCIDENTS, bannerPropsFor, bannersFor, bundledIncident, chipLabelFor, incidentAccounts, incidentRoleCounts, isSubjectOfHarm, toneFor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map